import { Formik } from 'formik';
import _ from 'lodash';
import React, { useRef } from 'react';
import * as Yup from 'yup';
import moment from 'moment';
import { ActionButton, Buttons, CancelButton, Field, Form, FormMessage, ModalCard, Radio } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm } from '~/hooks';
import { dateFormats, emptyStringToNull, mergeValues } from '~/utils';

function ProjectTaskCreateForm({ project, defaultValues, onClose, onSaved }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const firstFieldRef = useRef();
  const toast = useToast();

  async function handleSubmit(values) {
    try {
      form.submit(formRef.current.status.action);
      let body = values;
      if (defaultValues) {
        Object.assign(body, defaultValues);
      }
      body = emptyStringToNull({
        ..._.omit(body, [
          'parent',
          'taskCategory',
          ...(values.taskCategory?.type === 'rollup'
            ? ['start', 'end', 'statusId', 'isBillable']
            : values.taskCategory?.type === 'milestone'
              ? ['end', 'isBillable']
              : []),
        ]),
        parentId: values.parent?.id ?? null,
        projectTaskCategoryId: values.taskCategory?.id ?? null,
      });
      const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks(undefined).upsert(body);

      await onSaved(data, formRef.current.status.action);

      switch (formRef.current.status.action) {
        case 'save_and_new':
          formRef.current.resetForm();
          firstFieldRef.current.focus();
          toast.success('Task created successfully.');
          break;
      }

      form.done();
    } catch ({ message }) {
      form.error({ message });
    }
  }

  const initialValues = mergeValues(
    {
      end: null,
      isBillable: true,
      name: '',
      taskCategory: null,
      start: null,
      parent: null,
      statusId: 'not_started',
    },
    defaultValues,
  );

  const handleClose = () => dirtyCheck(onClose);

  return (
    <ModalCard title="New Task" onClose={handleClose}>
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          end: Yup.date()
            .label('End Date')
            .nullable()
            .when('start', (start, schema) =>
              start ? schema.min(Yup.ref('start'), 'End Date must be after Start Date') : schema,
            ),
          isBillable: Yup.bool(),
          name: Yup.string().label('Name').max(255).trim().required(),
          taskCategory: Yup.object().label('Task Category').nullable().required(),
          start: Yup.date().label('Start Date').nullable(),
        })}>
        {(formik) => {
          const isRollup = formik.values.taskCategory?.type === 'rollup';
          const isMilestone = formik.values.taskCategory?.type === 'milestone';

          const handleCategoryChange = ({ target: { value } }) => {
            formik.setFieldValue('taskCategory', value);
            if (value?.type === 'milestone' && formik.values.statusId === 'in_progress') {
              formik.setFieldValue('statusId', 'not_started');
            }
          };

          const handleStartChange = (value) => {
            const oldStart = formik.values.start;
            const oldEnd = formik.values.end;

            formik.setFieldValue('start', value);

            if (value && oldStart && oldEnd && moment(oldEnd).isSameOrAfter(oldStart)) {
              const diff = moment(value).diff(moment(oldStart), 'days');
              const newEnd = moment(oldEnd).add(diff, 'days').format(dateFormats.isoDate);
              formik.setFieldValue('end', newEnd);
            } else if (value && (!oldEnd || moment(value).isAfter(oldEnd))) {
              formik.setFieldValue('end', moment(value).format(dateFormats.isoDate));
            }
          };

          const submit = async (action) => {
            formik.setStatus({ action });
            formik.submitForm();
          };

          return (
            <Form>
              <ModalCard.Body>
                <>
                  <Form.Control>
                    <Field.Text
                      onChange={(event) => {
                        formik.handleChange(event);
                      }}
                      autoFocus
                      name="name"
                      style={{ minWidth: '37rem' }}
                      placeholder="Task Name"
                      maxLength={255}
                      ref={firstFieldRef}
                    />
                  </Form.Control>
                </>

                <Form.Control>
                  <Field.ProjectTaskCategorySelect
                    name="taskCategory"
                    placeholder="Task Category"
                    clearable={false}
                    useDefault
                    onChange={handleCategoryChange}
                  />
                </Form.Control>

                <Form.Control>
                  <Field.ProjectTaskSelect
                    name="parent"
                    placeholder="Parent Task"
                    project={project}
                    disabled={!!defaultValues?.parent}
                    clearable={!defaultValues?.parent}
                    disableMilestones
                  />
                </Form.Control>

                {!isRollup && (
                  <>
                    <Form.Control>
                      <Field.ProjectTaskStatusSelect
                        name="statusId"
                        placeholder="Status"
                        exclude={isMilestone ? ['in_progress'] : undefined}
                        disabled={!!defaultValues?.statusId}
                      />
                    </Form.Control>

                    <Form.Control>
                      {isMilestone ? (
                        <Field.DayPicker name="start" placeholder="Date" />
                      ) : (
                        <>
                          <Field.DayPicker name="start" placeholder="Start Date" onChange={handleStartChange} />
                          <Field.DayPicker name="end" placeholder="End Date" />
                        </>
                      )}
                    </Form.Control>

                    {!isMilestone && project.isBillable && (
                      <Form.Control>
                        <Field.RadioGroup name="isBillable">
                          <Radio value={true} label="Billable" />
                          <Radio value={false} label="Non-billable" />
                        </Field.RadioGroup>
                      </Form.Control>
                    )}
                  </>
                )}
                {status && <FormMessage.Error>{message || 'An error has occurred.'}</FormMessage.Error>}
              </ModalCard.Body>

              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={handleClose}>Close</CancelButton>

                  <ActionButton
                    isOutline
                    isLoading={isSubmitting === 'save_and_new'}
                    ok={saved === 'save_and_new'}
                    onClick={() => submit('save_and_new')}>
                    Save &amp; New
                  </ActionButton>

                  <ActionButton
                    isLoading={isSubmitting === 'save'}
                    ok={saved === 'save'}
                    onClick={() => submit('save')}>
                    Save
                  </ActionButton>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          );
        }}
      </Formik>
    </ModalCard>
  );
}

export default ProjectTaskCreateForm;
